import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { motion, useReducedMotion } from "motion/react";
import { Camera, Sparkles, Upload } from "lucide-react";
import {
  CREDIT_COSTS,
  getCurrencyExponent,
  type SupportedCurrencyCode,
} from "@repo/api-schema";
import { mapDraftToFormSeed } from "@repo/api-hooks";
import { downscaleToJpeg } from "../lib/downscaleImage";
import { useIsTouchDevice } from "../hooks/useIsTouchDevice";
import {
  useCreateUploadTicket,
  useMyCredits,
  useScanReceipt,
} from "../api/hooks";
import type { ExpenseFormInitialDraft } from "./ExpenseForm";

const SCAN_COST = CREDIT_COSTS.SCAN_COST;
const IMAGE_TYPES = "image/jpeg,image/png,image/webp,image/heic";
const PDF_TYPE = "application/pdf";
// The "Upload" picker accepts images and PDF in one OS dialog. "Take photo"
// stays separate: it needs `capture` to force the rear camera, which a combined
// input can't do without also hijacking plain file browsing.
const UPLOAD_TYPES = `${IMAGE_TYPES},${PDF_TYPE}`;

type UploadContentType =
  "image/jpeg" | "image/png" | "image/webp" | "image/heic" | "application/pdf";

/**
 * "Fill from a receipt" bar shown at the top of the New Expense screen. Lets the
 * user snap a photo (touch devices) or upload an image or PDF; runs the OCR +
 * LLM scan; and hands the resulting draft up via `onScanned` so the parent can
 * prefill the form in place (no separate scan page). Web can't earn credits —
 * when out, it points the user to the mobile app. See docs/receipt-scanning.md.
 */
export function ReceiptScanBar({
  group,
  onScanned,
}: {
  group: { id: string; currency: SupportedCurrencyCode };
  onScanned: (draft: ExpenseFormInitialDraft) => void;
}): ReactNode {
  const isTouch = useIsTouchDevice();
  const creditsQ = useMyCredits();
  const createTicket = useCreateUploadTicket();
  const scan = useScanReceipt(group.id);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Two inputs: one camera-capture (Take photo), one plain file picker that
  // takes images *and* PDF (Upload). They stay separate because the capture
  // input forces the rear camera, which would also hijack plain file browsing
  // if merged into one input.
  const cameraRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  const balance = creditsQ.data?.balance ?? 0;
  const canScan = balance >= SCAN_COST && !busy;

  async function onFile(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file || !canScan) return;
    await runScan(file);
  }

  async function runScan(file: File): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const isPdf = file.type === PDF_TYPE;
      // Images are downscaled + re-encoded to JPEG so a multi-MB camera photo
      // fits the upload cap; a PDF is uploaded as-is (downscaling would corrupt
      // it, and Textract reads single-page PDFs directly).
      const blob = isPdf ? file : await downscaleToJpeg(file);
      const contentType: UploadContentType = isPdf
        ? PDF_TYPE
        : normaliseImageType(blob.type);

      const ticket = await createTicket.mutateAsync({
        contentType,
        byteSize: blob.size,
      });
      const put = await fetch(ticket.uploadUrl, {
        method: "PUT",
        headers: { "content-type": contentType },
        body: blob,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);

      const result = await scan.mutateAsync({ objectKey: ticket.objectKey });

      const currency = (result.draft.currency ??
        group.currency) as SupportedCurrencyCode;
      const exponent = getCurrencyExponent(currency);
      const seed = mapDraftToFormSeed({
        draft: result.draft,
        groupCurrency: currency,
        participantIds: [],
      });
      onScanned({
        label: seed.label,
        currency: seed.currency,
        items: seed.items.map((it) => ({
          label: it.label,
          amount: (it.cost / 10 ** exponent).toFixed(exponent),
        })),
        lowConfidenceFields: seed.lowConfidenceFields,
        // Carry the translation through verbatim (issue #284) so the form can
        // show the translated label/items beside the originals. Money fields
        // aren't translated, so the seed mapping doesn't touch this.
        translation: result.draft.translation,
      });
      setBusy(false);
    } catch {
      setError("Couldn't read that receipt. Please try again.");
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 rounded-md border border-outline-variant/60 bg-surface-container-low p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-label-lg text-on-surface">Fill from a receipt</p>
        <span className="inline-flex items-center gap-1.5 text-label-sm text-on-surface-variant">
          <Sparkles className="size-4" />
          {creditsQ.isLoading ? "Checking credits…" : `${balance} credits`}
          <span aria-hidden="true">·</span>
          {SCAN_COST} per scan
        </span>
      </div>

      <input
        ref={cameraRef}
        type="file"
        accept={IMAGE_TYPES}
        capture="environment"
        className="hidden"
        onChange={onFile}
      />
      <input
        ref={uploadRef}
        type="file"
        accept={UPLOAD_TYPES}
        className="hidden"
        onChange={onFile}
      />

      {balance < SCAN_COST ? (
        <p className="rounded-md bg-surface-container px-3 py-2 text-body-sm text-on-surface-variant">
          You're out of credits. Earn more by watching a short video in the
          Tailspend mobile app, then come back to scan here.
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="text-body-sm text-error">
          {error}
        </p>
      ) : null}

      {busy ? (
        <ScanningStatus />
      ) : (
        <div className={`grid gap-2 ${isTouch ? "grid-cols-2" : ""}`}>
          {isTouch ? (
            <ScanTile
              icon={<Camera className="size-5" />}
              label="Take photo"
              disabled={!canScan}
              onClick={() => cameraRef.current?.click()}
            />
          ) : null}
          <ScanTile
            icon={<Upload className="size-5" />}
            label="Upload image or PDF"
            disabled={!canScan}
            onClick={() => uploadRef.current?.click()}
          />
        </div>
      )}
    </div>
  );
}

/** A single capture option in the scan bar. */
function ScanTile({
  icon,
  label,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  disabled: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-outline-variant/60 bg-surface-container-lowest px-3 py-4 text-label-md text-on-surface transition-colors hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50"
    >
      <span className="text-primary">{icon}</span>
      {label}
    </button>
  );
}

const SCAN_STAGES = [
  "Reading the receipt…",
  "Spotting line items…",
  "Adding up the total…",
  "Building your expense…",
];

/**
 * Inline scanning status that replaces the capture tiles while a scan runs:
 * three softly-pulsing dots plus cycling status copy, so the OCR + LLM wait
 * feels alive without a heavy full-screen overlay.
 */
function ScanningStatus(): ReactNode {
  const reduceMotion = useReducedMotion();
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setStage((s) => (s + 1) % SCAN_STAGES.length),
      1400,
    );
    return () => clearInterval(id);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center justify-center gap-3 py-6 text-center"
    >
      <div className="flex items-center gap-1.5">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            aria-hidden="true"
            className="size-2 rounded-full bg-primary"
            animate={reduceMotion ? undefined : { opacity: [0.35, 1, 0.35] }}
            transition={{
              duration: 1.05,
              ease: "easeInOut",
              repeat: Infinity,
              delay: i * 0.18,
            }}
          />
        ))}
      </div>
      <motion.p
        key={stage}
        initial={reduceMotion ? false : { opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-body-sm text-on-surface-variant"
      >
        {SCAN_STAGES[stage]}
      </motion.p>
    </div>
  );
}

/** Map a browser image File MIME to one of the contract's accepted types. */
function normaliseImageType(
  mime: string,
): Exclude<UploadContentType, "application/pdf"> {
  switch (mime) {
    case "image/jpeg":
    case "image/png":
    case "image/webp":
    case "image/heic":
      return mime;
    default:
      return "image/jpeg";
  }
}
